import { UpdateCompanyBrandEventDto } from "~/api/dto/company-brand-event/update-company-brand-event.dto"
import { useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import { useBrandEventsControllerGetCompanyConnectorsQuery } from "~/ui-rtk/api/brandEventApi"
import {
  useCompanyBrandEventsControllerGetCompanyBrandEventsQuery,
  useCompanyBrandEventsControllerUpsertCompanyBrandEventsMutation,
} from "~/ui-rtk/api/companyBrandEventApi"
import {
  useCompanyControllerUpdateCompanyMutation,
  useCompanyControllerGetCompanyByIdQuery,
} from "~/ui-rtk/api/companyApi"
import { type CompanyMetadataDto } from "~/ui-rtk/api/types"
import { isUnauthorizedError } from "~/ui-rtk/utils/http-utils"
import { useAuthControllerLogoutMutation } from "~/ui-rtk/api/authApi"
import { useStableNavigate } from "~/ui-rtk/utils/StableNavigateContext"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"

export function useCodeBlock(companyId: string) {
  // PLZ Keep formatting for formatting's sake!
  const formattedCodeBlock = useMemo(
    () => `<script
  id="marathon-data-pixel-script"
  async
  src="https://pixel.marathondataco.com/pixel.js?companyId=${companyId}"
></script>`,
    [companyId],
  )

  const codeBlock = `<script id="marathon-data-pixel-script" async src="https://pixel.marathondataco.com/pixel.js?companyId=${companyId}"></script>`
  return {
    codeBlock,
    formattedCodeBlock,
  }
}

export function useConnect() {
  const [companyIdSet, setCompanyIdSet] = useState<string | undefined>()
  const currentCompany = useCurrentCompany()
  const [logout] = useAuthControllerLogoutMutation()
  const navigate = useStableNavigate()

  const { data: allBrandEvents, isLoading: isBeLoading } =
    useBrandEventsControllerGetCompanyConnectorsQuery()
  const {
    data: companyBrandEvents,
    isLoading,
    refetch,
  } = useCompanyBrandEventsControllerGetCompanyBrandEventsQuery()
  const [updateCompanyBrandEvents] =
    useCompanyBrandEventsControllerUpsertCompanyBrandEventsMutation()

  const { data: companyData, refetch: refetchCompany } =
    useCompanyControllerGetCompanyByIdQuery(
      { id: currentCompany?.id ?? "" },
      { skip: !currentCompany?.id },
    )
  const [updateCompany] = useCompanyControllerUpdateCompanyMutation()

  useEffect(() => {
    if (companyIdSet && companyIdSet !== currentCompany?.id) {
      void refetch()
    }
    setCompanyIdSet(currentCompany?.id)
  }, [currentCompany?.id])

  const initialValues = useMemo(
    () => ({
      events: allBrandEvents?.map(brandEvent => {
        const companyBrandEvent = companyBrandEvents?.find(
          event => event.eventId === brandEvent.id,
        )
        if (companyBrandEvent) {
          return {
            id: companyBrandEvent.id,
            eventId: companyBrandEvent.eventId,
            isActive: companyBrandEvent.isActive,
          }
        }
        return {
          id: `${currentCompany?.id}-${brandEvent.id}`,
          eventId: brandEvent.id,
          isActive: false,
        }
      }),
    }),
    [currentCompany, allBrandEvents, companyBrandEvents],
  )

  const updateSettings = useCallback(
    async (values: UpdateCompanyBrandEventDto[]) => {
      if (!currentCompany?.id) {
        return
      }

      try {
        await updateCompanyBrandEvents({
          body: values,
        }).unwrap()
        toast.success("Brand Events Settings were updated")
        await refetch()
      } catch (error) {
        if (isUnauthorizedError(error)) {
          await logout()
          navigate("/")
          return
        }
        toast.error("Could not update Brand Events Settings")
      }
    },
    [currentCompany],
  )

  const getPixelData = useCallback(() => {
    try {
      const metadata = companyData?.metadata as
        | Record<string, unknown>
        | undefined
      const pixelData = metadata?.pixel_data || {}

      // Return the dynamic pixel data object, ensuring all values are arrays
      const result: Record<string, string[]> = {}
      for (const [platform, data] of Object.entries(pixelData)) {
        result[platform] = Array.isArray(data) ? (data as string[]) : []
      }

      return result
    } catch (error) {
      console.warn("Error getting pixel data:", error)
      return {}
    }
  }, [companyData])

  const savePixelData = useCallback(
    async (platform: string, pixelIds: string[]) => {
      if (!currentCompany?.id || !companyData) {
        return
      }

      try {
        const currentMetadata =
          (companyData.metadata as Record<string, unknown>) || {}
        const existingPixelData = currentMetadata.pixel_data || {}

        const updatedPixelData = {
          ...existingPixelData,
          [platform]: pixelIds,
        }

        const updatedMetadata = {
          ...currentMetadata,
          pixel_data: updatedPixelData,
        }

        await updateCompany({
          id: currentCompany.id,
          updateCompanyDto: {
            metadata: updatedMetadata as CompanyMetadataDto,
          },
        }).unwrap()

        toast.success(
          `${platform.charAt(0).toUpperCase() + platform.slice(1)} pixel IDs saved successfully`,
        )
        await refetchCompany()
      } catch (error) {
        if (isUnauthorizedError(error)) {
          await logout()
          navigate("/")
          return
        }
        toast.error(`Could not save ${platform} pixel IDs`)
      }
    },
    [
      currentCompany,
      companyData,
      updateCompany,
      refetchCompany,
      logout,
      navigate,
    ],
  )

  return {
    allBrandEvents,
    initialValues,
    companyBrandEvents,
    isLoading: isLoading || isBeLoading,
    updateSettings,
    getPixelData,
    savePixelData,
  }
}
